/* eslint-disable no-await-in-loop, no-constant-condition */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import OfflineUploadQueue from '@utils/OfflineUploadQueue/OfflineUploadQueue';
import { useNetworkState } from '@services/hooks.service';
import { API_URL } from '@App/app.constants';
import translator from '@App/app.translator';

import OfflineUploadQueueContext from './offline-upload-queue-provider.context';
import { computeBackoffMs, uploadItem } from './offline-upload-queue-provider.utils';

const MAX_ATTEMPTS = 5;
const BASE_DELAY_MS = 5000;
const MAX_DELAY_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 0.5 * 60 * 1000;
// How long an item can remain "inflight" before we assume the app crashed mid-upload.
const INFLIGHT_STALE_MS = 5 * 60 * 1000;

const queue = new OfflineUploadQueue();

function OfflineUploadQueueProvider({ children }) {
  const isOnline = useNetworkState();

  const [count, setCount] = useState(0);
  const processingRef = useRef(false);

  const refreshCount = useCallback(async () => {
    const newCount = await queue.count();
    setCount(newCount);
  }, []);

  const processQueue = useCallback(async () => {
    if (!isOnline || processingRef.current) return;

    processingRef.current = true;

    try {
      // Crash recovery: if the app was closed mid-upload, an item can remain inflight.
      // Once it is considered stale, move it back to failed so it can be retried.
      await queue.requeueInflightOlderThan(INFLIGHT_STALE_MS);

      let processedCount = 0;
      let failedCount = 0;

      while (true) {
        const item = await queue.claimNext();

        if (!item) {
          break;
        }

        try {
          await uploadItem({ apiUrl: API_URL, item });
          await queue.ack(item.id);
          processedCount += 1;
        } catch (error) {
          failedCount += 1;

          const attempts = item.attempts || 1;
          if (attempts >= MAX_ATTEMPTS) {
            await queue.fail(item.id, error.message, { dead: true, nextAttemptAt: null });
          } else {
            const delayMs = computeBackoffMs(attempts, {
              baseDelayMs: BASE_DELAY_MS,
              maxDelayMs: MAX_DELAY_MS,
            });
            const nextAttemptAt = Date.now() + delayMs;
            await queue.fail(item.id, error.message, { dead: false, nextAttemptAt });
          }
        } finally {
          await refreshCount();
        }
      }

      if (processedCount > 0) {
        toast.success(
          translator.translate('{{count}} offline {{upload}} completed successfully.', {
            count: processedCount,
            upload: translator.translate(processedCount === 1 ? 'upload' : 'uploads'), 
          }),
        );
      }

      if (failedCount > 0) {
        toast.error(
          translator.translate('{{count}} offline {{upload}} failed. Will retry.', {
            count: failedCount,
            upload: translator.translate(failedCount === 1 ? 'upload' : 'uploads'), 
          }),
        );
      }
    } finally {
      processingRef.current = false;
      await refreshCount();
    }
  }, [isOnline, refreshCount]);

  const enqueue = useCallback(async ({ type, payload }) => {
    const id = await queue.enqueue({ type, payload });
    await refreshCount();

    if (isOnline) {
      // Try immediately in case we are online.
      processQueue();
    }

    return id;
  }, [isOnline, processQueue, refreshCount]);

  // When connectivity returns, attempt processing.
  useEffect(() => {
    if (isOnline) {
      processQueue();
    }
  }, [isOnline, processQueue]);

  useEffect(() => {
    const intervalId = setInterval(processQueue, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [processQueue]);

  const value = useMemo(() => ({
    count,
    enqueue,
  }), [count, enqueue]);

  return (
    <OfflineUploadQueueContext.Provider value={value}>
      {children}
    </OfflineUploadQueueContext.Provider>
  );
}

export { useOfflineUploadQueue } from './offline-upload-queue-provider.context';
export default OfflineUploadQueueProvider;
